import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, selectCurrentUser } from '../../features/auth/authSlice';
import {
  HomeIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  Bars3Icon,
  XMarkIcon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { ClipboardList, Headphones, Sparkles, Megaphone, MessageCircle, MessageSquare, Phone, Send, Facebook, Instagram, Target, Users, Inbox } from 'lucide-react';
import { ThemeToggle } from '../ui/theme-toggle';
import { BrandPresetSelector } from '../ui/brand-preset-selector';
import { Logger } from '../../lib/utils';
import { ConfirmProvider } from '../ui/confirm-dialog';
import { getEffectiveModuleFeatures, hasWhatsAppModuleAccess } from '../../features/auth/moduleFeatures';
import { useBrandTheme } from '../providers/brand-theme-provider.utils';

export const MainLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { brandName } = useBrandTheme();

  // Cerrar sidebar al cambiar tamaño de ventana a desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className='h-screen bg-background overflow-hidden flex flex-col'>
      {/* Barra superior */}
      <header className='bg-card border-b border-border h-16 z-30 shadow-sm px-4 md:px-6 flex-shrink-0'>
        <div className='flex items-center justify-between h-full'>
          {/* Logo y toggle */}
          <div className='flex items-center gap-3'>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className='p-2 rounded-md text-foreground hover:bg-accent hover:text-accent-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50'
              aria-label='Toggle menú'
            >
              {sidebarOpen ? <XMarkIcon className='h-5 w-5' /> : <Bars3Icon className='h-5 w-5' />}
            </button>
            <Link to='/' className='flex items-center gap-2'>
              <div className='bg-primary rounded-md p-1.5 shadow-sm'>
                <BuildingOfficeIcon className='h-5 w-5 text-primary-foreground' />
              </div>
              <span className='font-semibold text-lg text-foreground hidden md:block'>
                {brandName}
              </span>
            </Link>
          </div>

          {/* Menú Usuario */}
          <div className='flex items-center gap-3'>
            <UserMenu />
          </div>
        </div>
      </header>

      <div className='flex flex-1 overflow-hidden'>
        {/* Overlay para móvil */}
        {sidebarOpen && (
          <button
            className='fixed inset-0 bg-foreground/10 backdrop-blur-sm z-20 lg:hidden cursor-default'
            onClick={() => setSidebarOpen(false)}
            aria-label='Cerrar menú'
          />
        )}

        {/* Sidebar */}
        <aside
          style={{
            position: 'fixed',
            top: '4rem',
            left: sidebarOpen ? '0' : '-16rem',
            width: '16rem',
            height: 'calc(100vh - 4rem)',
            zIndex: 20,
            transition: 'left 0.3s ease-in-out',
            transform: 'none',
          }}
          className='bg-sidebar text-sidebar-foreground border-r border-sidebar-border shadow-sm overflow-y-auto lg:static lg:top-auto lg:left-auto lg:h-full lg:pt-0'
        >
          <SidebarContent closeSidebar={() => setSidebarOpen(false)} />
        </aside>

        {/* Contenido principal */}
        <main
          style={{
            marginLeft: sidebarOpen ? '16rem' : '0',
            transition: 'margin-left 0.3s ease-in-out',
          }}
          className={`flex-1 overflow-auto p-4 sm:p-6 md:p-8 w-full bg-background lg:ml-0`}
        >
          <div className='max-w-7xl mx-auto'>
            <ConfirmProvider>
              <Outlet />
            </ConfirmProvider>
          </div>
        </main>
      </div>
    </div>
  );
};

// Menú de usuario
const UserMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const getRoleLabel = (role?: string): string => {
    if (!role) return 'Usuario';
    switch (role) {
      case 'SUPERADMIN':
        return 'Superadministrador';
      case 'ADMIN':
        return 'Administrador';
      default:
        return 'Usuario';
    }
  };

  return (
    <div className='relative flex items-center gap-2'>
      {user?.role === 'SUPERADMIN' && <BrandPresetSelector className='hidden sm:flex' />}
      <ThemeToggle />

      <button
        className='flex items-center gap-2 p-2 rounded-full hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50'
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <div className='w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium shadow-sm'>
          {user?.email?.charAt(0).toUpperCase() || 'U'}
        </div>
        <span className='text-sm font-medium text-foreground hidden md:block'>
          {user?.email?.split('@')[0] || 'Usuario'}
        </span>
      </button>

      {menuOpen && (
        <>
          <div className='fixed inset-0 z-40' onClick={() => setMenuOpen(false)}></div>
          <div className='absolute right-0 mt-2 w-64 bg-card rounded-lg shadow-sm border border-border z-50 top-full'>
            <div className='p-4 border-b border-border bg-muted'>
              <p className='text-sm font-medium text-foreground'>{user?.email}</p>
              <p className='text-xs text-muted-foreground mt-1'>{getRoleLabel(user?.role)}</p>
            </div>
            <div className='p-2'>
              <Link
                to='/perfil'
                className='flex items-center gap-2 p-2.5 text-sm text-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors'
                onClick={() => setMenuOpen(false)}
              >
                <UserCircleIcon className='h-5 w-5 text-muted-foreground' />
                Mi Perfil
              </Link>
              <button
                onClick={handleLogout}
                className='w-full flex items-center gap-2 p-2.5 text-sm text-destructive hover:bg-destructive/10 rounded-md transition-colors mt-1'
              >
                <ArrowRightOnRectangleIcon className='h-5 w-5' />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Contenido de la sidebar
interface SidebarContentProps {
  closeSidebar: () => void;
}

const SidebarContent = ({ closeSidebar }: SidebarContentProps) => {
  const { brandName } = useBrandTheme();
  const user = useSelector(selectCurrentUser);
  const canAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
  const isOperator = user?.role === 'OPERATOR';
  const isTenantUser = user?.role === 'USER';
  const moduleFeatures = getEffectiveModuleFeatures(user);
  const canUseChatbot = user?.role === 'SUPERADMIN' || moduleFeatures.includes('chatbot');
  const canUseInbox =
    isOperator
    || user?.role === 'SUPERADMIN'
    || (user?.role === 'ADMIN' && canUseChatbot);
  const canUseMesa =
    user?.role === 'SUPERADMIN' || moduleFeatures.includes('helpdesk');
  const canUseChatwoot =
    user?.role === 'SUPERADMIN' || moduleFeatures.includes('chatwoot-integration');
  const canUseWhatsApp = hasWhatsAppModuleAccess(user);
  const canUseTelegram =
    user?.role === 'SUPERADMIN' || moduleFeatures.includes('telegram-integration');
  const canUseFacebook =
    user?.role === 'SUPERADMIN' || moduleFeatures.includes('facebook-integration');
  const canUseInstagram =
    user?.role === 'SUPERADMIN' || moduleFeatures.includes('instagram-integration');
  const canUseContactAgent =
    user?.role === 'SUPERADMIN' || moduleFeatures.includes('contact-agent');
  const leadsFeatureOn = user?.role === 'SUPERADMIN' || moduleFeatures.includes('leads');
  const canUseLeads = canAdmin && leadsFeatureOn;
  // El vendedor (OPERATOR) accede solo al tablero de casos, sin configuración.
  const canUseLeadsBoard = isOperator && leadsFeatureOn;

  Logger.debug('Estado del usuario en Sidebar:', {
    role: user?.role,
  });

  return (
    <div className='py-6 px-3'>
      <nav className='space-y-6'>
        {/* Navegación principal */}
        <div className='space-y-0.5'>
          {isTenantUser ? (
            <NavItem
              to='/chatbots'
              icon={ChatBubbleLeftRightIcon}
              text='Mis Chatbots'
              closeSidebar={closeSidebar}
            />
          ) : isOperator ? (
            <>
              <NavItem
                to='/admin/inbox'
                icon={ChatBubbleLeftRightIcon}
                text='Inbox'
                closeSidebar={closeSidebar}
              />
              {canUseLeadsBoard && (
                <NavItem
                  to='/admin/leads/board'
                  icon={Target}
                  text='Embudo de leads'
                  closeSidebar={closeSidebar}
                />
              )}
            </>
          ) : (
            <>
              <NavItem to='/' icon={HomeIcon} text='Dashboard' end closeSidebar={closeSidebar} />
              {canUseInbox ? (
                <NavItem to='/admin/inbox' icon={Inbox} text='Inbox' closeSidebar={closeSidebar} />
              ) : null}
            </>
          )}
        </div>

        {canAdmin && (
          <>
            {/* Agentes: crear y configurar lo que atiende */}
            <NavSection
              title='Agentes'
              closeSidebar={closeSidebar}
              items={[
                { show: user?.role === 'SUPERADMIN', to: '/admin/recetas', icon: Sparkles, text: 'Crear agente' },
                { show: canUseChatbot, to: '/admin/chatbot', icon: ChatBubbleLeftRightIcon, text: 'Chatbots' },
                { show: canUseContactAgent, to: '/admin/contact-agent', icon: Phone, text: 'Cobranzas', end: true },
                { show: canUseLeads, to: '/admin/leads', icon: Target, text: 'Embudo de leads', end: true },
                { show: canUseMesa, to: '/admin/mesa', icon: Headphones, text: 'Mesa de ayuda' },
              ]}
            />

            {/* Operación: el trabajo de todos los días */}
            <NavSection
              title='Operación'
              closeSidebar={closeSidebar}
              items={[
                { show: canUseContactAgent, to: '/admin/contact-agent/inbox', icon: ClipboardList, text: 'Bandeja de trabajo' },
                { show: canUseContactAgent, to: '/admin/campaigns', icon: Megaphone, text: 'Campañas' },
                { show: canUseContactAgent, to: '/admin/contact-agent/contacts', icon: Users, text: 'Contactos' },
              ]}
            />

            {/* Canales: por dónde llegan y salen los mensajes */}
            <NavSection
              title='Canales'
              closeSidebar={closeSidebar}
              items={[
                { show: canUseWhatsApp, to: '/admin/whatsapp', icon: MessageCircle, text: 'WhatsApp' },
                {
                  show: canUseContactAgent,
                  to: '/admin/contact-agent/proactive-templates',
                  icon: MessageSquare,
                  text: 'Plantillas fuera de ventana',
                },
                { show: canUseTelegram, to: '/admin/telegram', icon: Send, text: 'Telegram' },
                { show: canUseFacebook, to: '/admin/facebook', icon: Facebook, text: 'Facebook' },
                { show: canUseInstagram, to: '/admin/instagram', icon: Instagram, text: 'Instagram' },
                { show: canUseChatwoot, to: '/admin/chatwoot', icon: MessageSquare, text: 'Chatwoot' },
              ]}
            />

            {/* Administración: clientes y personas de la plataforma */}
            <NavSection
              title='Administración'
              closeSidebar={closeSidebar}
              items={[
                { show: user?.role === 'SUPERADMIN', to: '/admin/tenants', icon: BuildingOfficeIcon, text: 'Tenants' },
                { show: true, to: '/admin/users', icon: UsersIcon, text: 'Usuarios' },
              ]}
            />
          </>
        )}

        {/* Sección de configuración */}
        <div>
          <h3 className='px-4 text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider'>
            Configuración
          </h3>
          <div className='mt-3 space-y-1'>
            <NavItem
              to='/perfil'
              icon={UserCircleIcon}
              text='Mi Perfil'
              closeSidebar={closeSidebar}
            />
          </div>
        </div>
      </nav>

      {/* Footer con versión */}
      <div className='mt-auto'>
        <div className='border-t border-sidebar-border pt-4'>
          <p className='text-xs text-sidebar-foreground/50 text-center'>
            {brandName} v1.0
          </p>
        </div>
      </div>
    </div>
  );
};

// Item de navegación
interface NavItemProps {
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  text: string;
  badge?: string;
  /** Solo activo en esta ruta exacta (no en sus subpáginas, que tienen ítem propio). */
  end?: boolean;
  closeSidebar: () => void;
}

type NavSectionItem = Omit<NavItemProps, 'closeSidebar' | 'badge'> & { show: boolean };

// Sección del menú: no se muestra si el usuario no tiene ninguno de sus ítems.
const NavSection = ({
  title,
  items,
  closeSidebar,
}: {
  title: string;
  items: NavSectionItem[];
  closeSidebar: () => void;
}) => {
  const visible = items.filter(item => item.show);
  if (visible.length === 0) {
    return null;
  }
  return (
    <div>
      <h3 className='px-4 text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider'>{title}</h3>
      <div className='mt-2 space-y-0.5'>
        {visible.map(item => (
          <NavItem
            key={item.to}
            to={item.to}
            icon={item.icon}
            text={item.text}
            end={item.end}
            closeSidebar={closeSidebar}
          />
        ))}
      </div>
    </div>
  );
};

const NavItem = ({ to, icon: Icon, text, badge, end, closeSidebar }: NavItemProps) => {
  const handleClick = () => {
    if (window.innerWidth < 1024) {
      closeSidebar();
    }
  };

  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center justify-between px-4 py-2.5 rounded-md transition-colors ${
          isActive
            ? 'bg-sidebar-active text-white font-medium'
            : 'text-sidebar-foreground/80 hover:bg-white/10'
        }`
      }
      onClick={handleClick}
    >
      {({ isActive }) => (
        <>
          <div className='flex items-center'>
            <Icon
              className={`h-5 w-5 mr-3 flex-shrink-0 ${
                isActive ? 'text-white' : 'text-sidebar-foreground/60'
              }`}
            />
            <span className='text-sm'>{text}</span>
          </div>

          {badge && (
            <span className='ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary'>
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
};
